import React from 'react';
import { Info, MapPin, Gift, ArrowLeft } from 'lucide-react';
import { membershipNeedsVoucherTab } from './membershipFormTypes';

export type MembershipTabId = 'info' | 'brand' | 'voucher';

interface MembershipFormLayoutProps {
  activeTab: MembershipTabId;
  onTabChange: (tab: MembershipTabId) => void;
  membershipCode: string | null;
  rewardType: string;
  mappedBrandCount: number;
  onBackClick: () => void;
  children: React.ReactNode;
}

const TABS: { id: MembershipTabId; label: string; icon: React.ReactNode }[] = [
  { id: 'info', label: '멤버십 정보', icon: <Info className="w-4 h-4" /> },
  { id: 'brand', label: '브랜드 매핑', icon: <MapPin className="w-4 h-4" /> },
  { id: 'voucher', label: '교환권 설정', icon: <Gift className="w-4 h-4" /> },
];

const MembershipFormLayout: React.FC<MembershipFormLayoutProps> = ({
  activeTab,
  onTabChange,
  membershipCode,
  rewardType,
  mappedBrandCount,
  onBackClick,
  children,
}) => {
  const isBrandTabEnabled = !!membershipCode;
  const isVoucherTabEnabled =
    !!membershipCode && mappedBrandCount > 0 && membershipNeedsVoucherTab(rewardType);

  const getTabDisabledReason = (tabId: MembershipTabId): string | null => {
    if (tabId === 'brand' && !isBrandTabEnabled) {
      return '멤버십 정보를 먼저 저장해주세요.';
    }
    if (tabId === 'voucher' && !isVoucherTabEnabled) {
      if (!membershipCode) return '멤버십 정보를 먼저 저장해주세요.';
      if (mappedBrandCount === 0) return '브랜드를 먼저 매핑해주세요.';
      if (!membershipNeedsVoucherTab(rewardType)) {
        return '리워드 방식이 스탬프인 경우에만 설정 가능합니다.';
      }
    }
    return null;
  };

  return (
    <div className="space-y-6">
      {/* 멤버십 목록으로 - 탭 상단에 항상 표시 */}
      <div className="mb-4">
        <button
          type="button"
          onClick={onBackClick}
          className="flex items-center text-sm text-gray-600 hover:text-gray-900 transition-colors"
        >
          <ArrowLeft className="w-4 h-4 mr-1" />
          멤버십 목록으로
        </button>
      </div>
      {/* Tab Navigation */}
      <div className="border-b border-gray-200">
        <nav className="flex gap-1" aria-label="탭">
          {TABS.map((tab) => {
            const disabled =
              (tab.id === 'brand' && !isBrandTabEnabled) ||
              (tab.id === 'voucher' && !isVoucherTabEnabled);
            const disabledReason = getTabDisabledReason(tab.id);
            const isActive = activeTab === tab.id;

            return (
              <button
                key={tab.id}
                type="button"
                onClick={() => !disabled && onTabChange(tab.id)}
                disabled={disabled}
                title={disabledReason || undefined}
                className={`
                  flex items-center gap-2 px-4 py-3 text-sm font-medium rounded-t-lg border-b-2 -mb-px transition-colors
                  ${isActive
                    ? 'border-blue-600 text-blue-600 bg-blue-50'
                    : disabled
                    ? 'border-transparent text-gray-400 cursor-not-allowed'
                    : 'border-transparent text-gray-600 hover:text-gray-900 hover:bg-gray-50 hover:border-blue-600'
                  }
                `}
              >
                {tab.icon}
                {tab.label}
              </button>
            );
          })}
        </nav>
      </div>

      {/* Tab Content */}
      <div>{children}</div>
    </div>
  );
};

export default MembershipFormLayout;
